import * as THREE from 'three';
import { events } from '../core/events.js';
import { groundRadius } from './placement.js';
import { colliders } from './colliders.js';
import { spawnPickup } from './pickups.js';
import { COMM_PARTS } from '../game/commParts.js';

// A larger, irregular alien pond — a kidney-shaped patch of still teal
// water nestled in a damp shore, with floating lily pads, thin reeds
// around the edge, and a school of fish gliding beneath the surface.
// Water ripples, lily pads bob, fish wander in loops, and the deepest
// ring carries a faint bioluminescent emissive so it reads at night.
// Emits 'pond:discovered' the first time the player gets close.

const UP_Y = new THREE.Vector3(0, 1, 0);
// Southern-hemisphere latitude −30°, longitude 60°. The pond, ruins
// and cave form an equilateral triangle at the same latitude, 120° of
// longitude apart — evenly spaced around the planet.
export const POND_DIRECTION = new THREE.Vector3(
  Math.cos(-30 * Math.PI / 180) * Math.cos(60 * Math.PI / 180),
  Math.sin(-30 * Math.PI / 180),
  Math.cos(-30 * Math.PI / 180) * Math.sin(60 * Math.PI / 180),
).normalize();
const DISCOVERY_RANGE = 6.5;

// Radii for the three concentric shore layers. All three use the same
// noise seed with the same jitter coefficient so the rings stay
// concentric even when irregular — water never bulges past sand, etc.
const WATER_R = 2.9;
const SAND_R  = 3.4;
const SHORE_R = 3.85;
const SHAPE_JITTER = 0.38;

let pondWorldPos = null;
let pondGroup    = null;
let discovered   = false;

// Module-scope handles for the update loop.
let waterMesh = null;
let waterGeo  = null;
const waterBaseY = [];
const lilyPads   = [];
const fishList   = [];

// Lure-cast puzzle state — the Power Cell rides a raft lily pad. Casting
// the Lure Bloom pulls the raft from pond centre toward the player's side
// so the part becomes reachable.
const CAST_DURATION = 6.0;
let raftGroup        = null;
let raftLocal        = new THREE.Vector3();   // current local pos (x,0,z)
let raftStartLocal   = new THREE.Vector3();
let raftTargetLocal  = new THREE.Vector3();
let powerCellEntry   = null;
let lureState        = 'idle';                 // 'idle' | 'cast' | 'arrived'
let castStartTime    = -1;

// Shared noise used by the shape patches. Dominant low-frequency terms
// create big asymmetric lobes (one side pushed out, a kidney waist, a
// third pinched lobe) — reads as a proper natural pond rather than a
// bumpy circle. Carefully-chosen phases avoid the frequencies lining up
// into a symmetric shape. Same jitter across all layers → concentric.
function rimNoise(angle) {
  return Math.sin(angle + 1.1) * 0.50          // big asymmetric offset
       + Math.sin(angle * 2 + 2.7) * 0.28      // kidney waist
       + Math.sin(angle * 3 + 5.4) * 0.15      // third lobe
       + Math.sin(angle * 7 + 0.9) * 0.07;     // subtle rim detail
}

// Evaluate the water boundary radius at an angle (used to constrain fish).
function waterRadiusAt(angle) {
  return WATER_R * (1 + SHAPE_JITTER * rimNoise(angle));
}

// True if the player is standing on the shore (close to the pond from
// outside). Used by the interaction system to gate the "Cast Lure" prompt.
export function isPlayerAtPondEdge(worldPos) {
  if (!pondWorldPos) return false;
  const d = worldPos.distanceTo(pondWorldPos);
  return d < SHORE_R + 2.2;
}

export function canCastLure() {
  return lureState === 'idle';
}

// Begin the lure-cast animation. The raft drifts from the pond centre
// toward the player's side of the shore over CAST_DURATION seconds.
export function castLure(playerWorldPos) {
  if (!pondGroup || lureState !== 'idle') return false;

  // Convert player position into pond-local XZ to get the cast direction.
  const localPlayer = playerWorldPos.clone()
    .sub(pondGroup.position)
    .applyQuaternion(pondGroup.quaternion.clone().invert());
  const dx = localPlayer.x;
  const dz = localPlayer.z;
  const dist = Math.hypot(dx, dz);
  if (dist < 1e-4) return false;
  const angle = Math.atan2(dz, dx);

  // Target sits 70% out along the water radius at the cast angle so the
  // raft always stops inside the water but close enough to the shore
  // for the player to reach the Power Cell.
  const targetR = waterRadiusAt(angle) * 0.7;
  raftTargetLocal.set(
    Math.cos(angle) * targetR,
    raftStartLocal.y,
    Math.sin(angle) * targetR,
  );

  lureState     = 'cast';
  castStartTime = performance.now() / 1000;
  if (powerCellEntry) powerCellEntry.locked = false;
  events.emit('pond:lure-cast');
  return true;
}

export function createPond(scene, planet) {
  const group = new THREE.Group();
  pondGroup = group;

  const groundR = groundRadius(planet, POND_DIRECTION);
  group.quaternion.setFromUnitVectors(UP_Y, POND_DIRECTION);
  group.position.copy(POND_DIRECTION).multiplyScalar(groundR);
  scene.add(group);

  // ---- Materials ----
  const shoreMat = new THREE.MeshStandardMaterial({
    color: 0x5a4228, roughness: 1, flatShading: true,                 // damp mud
  });
  const sandMat = new THREE.MeshStandardMaterial({
    color: 0x8a7554, roughness: 1, flatShading: true,                 // wet sand
  });
  const waterMat = new THREE.MeshStandardMaterial({
    color: 0x3a90a8, roughness: 0.15, metalness: 0.55,
    transparent: true, opacity: 0.9,
    emissive: 0x184050, emissiveIntensity: 0.5,
    flatShading: true,
  });
  const stoneMat = new THREE.MeshStandardMaterial({
    color: 0x595f68, roughness: 0.9, flatShading: true,
  });
  const padMat = new THREE.MeshStandardMaterial({
    color: 0x4a8a58, roughness: 0.8, flatShading: true,
  });
  const padUnder = new THREE.MeshStandardMaterial({
    color: 0x2a4030, roughness: 1, flatShading: true,
  });
  const bloomMat = new THREE.MeshStandardMaterial({
    color: 0xf4d6ea, roughness: 0.7,
    emissive: 0xb890a8, emissiveIntensity: 0.35,
  });
  const reedMat = new THREE.MeshStandardMaterial({
    color: 0x567a3a, roughness: 0.9, flatShading: true,
  });
  const reedTipMat = new THREE.MeshStandardMaterial({
    color: 0x8c7a4a, roughness: 0.9, flatShading: true,
  });
  const fishMat = new THREE.MeshStandardMaterial({
    color: 0x182230, roughness: 0.7, metalness: 0.1, flatShading: true,
  });

  // ---- Irregular patch helper (uses shared rim noise) ----
  function makePatch(radius, segments, material) {
    const geo = new THREE.CircleGeometry(radius, segments);
    const pos = geo.attributes.position;
    for (let i = 1; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const angle = Math.atan2(z, x);
      const f = 1 + SHAPE_JITTER * rimNoise(angle);
      pos.setX(i, x * f);
      pos.setZ(i, z * f);
    }
    pos.needsUpdate = true;
    return { mesh: new THREE.Mesh(geo, material), geo };
  }

  // ---- Terrain-conforming helper ----
  // The pond group is a tangent plane at POND_DIRECTION. At the edges,
  // that flat plane lifts visibly above the curved mesh surface. Walking
  // each vertex through local→world→raycast→local produces a patch that
  // drapes onto the real terrain instead of floating above it.
  const _v = new THREE.Vector3();
  function conformPatchToTerrain(mesh, hover = 0) {
    mesh.updateMatrixWorld(true);
    const pos = mesh.geometry.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      _v.set(pos.getX(i), pos.getY(i), pos.getZ(i));
      mesh.localToWorld(_v);
      const terrainR = groundRadius(planet, _v.clone().normalize());
      _v.copy(_v).normalize().multiplyScalar(terrainR + hover);
      mesh.worldToLocal(_v);
      pos.setXYZ(i, _v.x, _v.y, _v.z);
    }
    pos.needsUpdate = true;
    mesh.geometry.computeVertexNormals();
  }

  // Snap a local (x, z) tangent point to the actual mesh surface and
  // return the group-local coordinates — used for stones, reeds, etc.
  const _tmpLocal = new THREE.Vector3();
  const _tmpWorld = new THREE.Vector3();
  const _invQuat = group.quaternion.clone().invert();
  function snapToGround(localX, localZ, hover = 0) {
    _tmpLocal.set(localX, 0, localZ).applyQuaternion(group.quaternion);
    _tmpWorld.copy(group.position).add(_tmpLocal);
    const dir = _tmpWorld.clone().normalize();
    const gr = groundRadius(planet, dir);
    const worldTarget = dir.multiplyScalar(gr + hover);
    return worldTarget.sub(group.position).applyQuaternion(_invQuat);
  }

  // ---- Shore layers (damp mud → wet sand → water) ----
  const shore = makePatch(SHORE_R, 96, shoreMat).mesh;
  shore.rotation.x = -Math.PI / 2;
  shore.position.y = -0.04;
  shore.receiveShadow = true;
  group.add(shore);
  conformPatchToTerrain(shore, 0.01);          // drape onto real terrain

  const sand = makePatch(SAND_R, 88, sandMat).mesh;
  sand.rotation.x = -Math.PI / 2;
  sand.position.y = -0.025;
  group.add(sand);
  conformPatchToTerrain(sand, 0.02);           // slightly above shore

  // ---- Stones around the rim ----
  const stoneCount = 14;
  for (let i = 0; i < stoneCount; i++) {
    // Anchor each stone to the actual jittered shore rim so they hug the
    // irregular edge instead of sitting on a clean circle.
    const a = (i / stoneCount) * Math.PI * 2 + Math.random() * 0.35;
    const rimR = SAND_R * (1 + SHAPE_JITTER * rimNoise(a));
    const rOff = (Math.random() - 0.3) * 0.35;
    const size = 0.12 + Math.random() * 0.14;
    const stone = new THREE.Mesh(
      new THREE.DodecahedronGeometry(size), stoneMat,
    );
    // Snap to the actual mesh surface so the stone doesn't float above
    // the planet's curvature at the pond edge.
    stone.position.copy(snapToGround(
      Math.cos(a) * (rimR + rOff),
      Math.sin(a) * (rimR + rOff),
      -size * 0.2,
    ));
    stone.rotation.set(Math.random() * 2, Math.random() * 2, Math.random() * 2);
    stone.scale.set(
      0.9 + Math.random() * 0.3,
      0.5 + Math.random() * 0.4,
      0.9 + Math.random() * 0.3,
    );
    stone.castShadow = true;
    group.add(stone);
  }

  // ---- Water surface (animated) ----
  const waterPatch = makePatch(WATER_R, 88, waterMat);
  waterMesh = waterPatch.mesh;
  waterGeo  = waterPatch.geo;
  waterMesh.rotation.x = -Math.PI / 2;
  waterMesh.position.y = -0.005;
  waterMesh.receiveShadow = true;
  group.add(waterMesh);

  // Record the base Y for each vertex so ripples don't drift.
  {
    const pos = waterGeo.attributes.position;
    waterBaseY.length = 0;
    for (let i = 0; i < pos.count; i++) waterBaseY.push(pos.getY(i));
  }

  // ---- Lily pads ----
  const padCount = 9;
  for (let i = 0; i < padCount; i++) {
    const a = (i / padCount) * Math.PI * 2 + Math.random() * 0.6;
    const r = 0.6 + Math.random() * (WATER_R * 0.75 - 0.6);
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    const size = 0.28 + Math.random() * 0.18;

    const padGroup = new THREE.Group();
    padGroup.position.set(x, 0.01, z);
    padGroup.rotation.y = Math.random() * Math.PI * 2;
    group.add(padGroup);

    const pad = new THREE.Mesh(
      new THREE.CircleGeometry(size, 20, 0, Math.PI * 1.85),
      padMat,
    );
    pad.rotation.x = -Math.PI / 2;
    padGroup.add(pad);

    const underside = new THREE.Mesh(
      new THREE.CircleGeometry(size, 20, 0, Math.PI * 1.85),
      padUnder,
    );
    underside.rotation.x = Math.PI / 2;
    underside.position.y = -0.012;
    padGroup.add(underside);

    if (Math.random() < 0.45) {
      const bloom = new THREE.Mesh(
        new THREE.SphereGeometry(size * 0.24, 10, 8), bloomMat,
      );
      bloom.scale.y = 0.55;
      bloom.position.y = size * 0.12;
      padGroup.add(bloom);
    }

    lilyPads.push({
      group: padGroup,
      baseY: padGroup.position.y,
      phase: Math.random() * Math.PI * 2,
    });
  }

  // ---- Reeds — grouped into 3 patches along the rim ----
  // Natural ponds don't have evenly-ringed reeds; they grow in clumps at
  // specific spots. Pick 3 patch-angles around the rim and spawn a
  // concentrated cluster at each.
  const reedPatchAngles = [0.4, 2.3, 4.7];
  for (const patchAngle of reedPatchAngles) {
    const arc = 0.9;                         // radians along the rim
    const stalksPerPatch = 14 + Math.floor(Math.random() * 6);
    for (let s = 0; s < stalksPerPatch; s++) {
      const ca = patchAngle + (Math.random() - 0.5) * arc;
      const rimR = SAND_R * (1 + SHAPE_JITTER * rimNoise(ca));
      const cr = rimR - 0.15 + Math.random() * 0.35;
      const cx = Math.cos(ca) * cr;
      const cz = Math.sin(ca) * cr;
      const height = 0.45 + Math.random() * 0.5;
      // Snap the reed's base to the actual ground, then build the stalk
      // upward from there. Without this the stalks float at the pond
      // edge because the flat pond plane lifts above the curved terrain.
      const basePos = snapToGround(cx, cz, 0);
      const reed = new THREE.Mesh(
        new THREE.CylinderGeometry(0.015, 0.02, height, 5), reedMat,
      );
      reed.position.set(basePos.x, basePos.y + height / 2, basePos.z);
      reed.rotation.z = (Math.random() - 0.5) * 0.25;
      reed.rotation.x = (Math.random() - 0.5) * 0.15;
      reed.castShadow = true;
      group.add(reed);

      const tip = new THREE.Mesh(
        new THREE.ConeGeometry(0.035, 0.12, 5), reedTipMat,
      );
      tip.position.set(basePos.x, basePos.y + height + 0.05, basePos.z);
      tip.rotation.copy(reed.rotation);
      group.add(tip);
    }
  }

  // ---- Fish ----
  // Each fish lives in the pond's local XZ plane, wandering with gentle
  // turns and arcing back toward the centre when it gets near the jittered
  // water boundary. Rendered just above the water surface so it reads as a
  // dark silhouette gliding under the ripples.
  fishList.length = 0;
  const fishCount = 7;
  for (let i = 0; i < fishCount; i++) {
    const mesh = makeFishMesh(fishMat);
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * (WATER_R * 0.5);
    mesh.position.set(Math.cos(a) * r, 0.006, Math.sin(a) * r);
    group.add(mesh);

    fishList.push({
      mesh,
      x: Math.cos(a) * r,
      z: Math.sin(a) * r,
      heading: Math.random() * Math.PI * 2,
      turnRate: (Math.random() - 0.5) * 0.6,
      turnTimer: 1 + Math.random() * 2,
      speed: 0.35 + Math.random() * 0.25,
      wigglePhase: Math.random() * Math.PI * 2,
    });
  }

  pondWorldPos = group.position.clone();
  discovered = false;

  // ---- Raft lily pad (holds the Power Cell) ----
  // A larger pad anchored at the pond centre with a small stone pedestal
  // on top. Starts here and drifts toward whichever shore the player
  // casts from; the Power Cell pickup rides on top.
  raftGroup = new THREE.Group();
  raftStartLocal.set(0, 0.012, 0);
  raftTargetLocal.copy(raftStartLocal);
  raftLocal.copy(raftStartLocal);
  raftGroup.position.copy(raftLocal);
  group.add(raftGroup);

  const raftPad = new THREE.Mesh(
    new THREE.CircleGeometry(0.55, 28),
    new THREE.MeshStandardMaterial({
      color: 0x3e7a4c, roughness: 0.8, flatShading: true,
    }),
  );
  raftPad.rotation.x = -Math.PI / 2;
  raftPad.receiveShadow = true;
  raftGroup.add(raftPad);

  const raftPadUnder = new THREE.Mesh(
    new THREE.CircleGeometry(0.55, 28),
    new THREE.MeshStandardMaterial({
      color: 0x223524, roughness: 1, flatShading: true,
    }),
  );
  raftPadUnder.rotation.x = Math.PI / 2;
  raftPadUnder.position.y = -0.012;
  raftGroup.add(raftPadUnder);

  // Small stone pedestal where the Power Cell sits.
  const pedestal = new THREE.Mesh(
    new THREE.CylinderGeometry(0.16, 0.2, 0.09, 12),
    new THREE.MeshStandardMaterial({
      color: 0x595f68, roughness: 0.9, flatShading: true,
    }),
  );
  pedestal.position.y = 0.048;
  pedestal.castShadow = true;
  raftGroup.add(pedestal);

  pondWorldPos = group.position.clone();
  discovered = false;

  // Collision — because the water is irregular a single central cylinder
  // can't match the shape, so we lay down a ring of 8 overlapping cylinders
  // each sized to the local water radius at its angle. Adjacent cylinders
  // overlap heavily so there are no gaps the player can slip through.
  // Height is set above peak-jump altitude so the pond can't be vaulted:
  // max jump ≈ 2.4 (JUMP_V²/2·GRAVITY), so 2.8 safely clears that.
  const COLLIDER_SAMPLES = 8;
  const _localOff = new THREE.Vector3();
  for (let i = 0; i < COLLIDER_SAMPLES; i++) {
    const a = (i / COLLIDER_SAMPLES) * Math.PI * 2;
    const rimR = WATER_R * (1 + SHAPE_JITTER * rimNoise(a));
    _localOff.set(Math.cos(a) * rimR * 0.5, 0, Math.sin(a) * rimR * 0.5)
             .applyQuaternion(group.quaternion);
    colliders.push({
      pos: group.position.clone().add(_localOff),
      up:  POND_DIRECTION.clone(),
      r:   rimR * 0.62,
      h:   2.8,
    });
  }

  // ---- Power Cell pickup, mounted on the raft ----
  // Locked until the player casts the lure, so jumping into the middle
  // of the pond doesn't let you shortcut the puzzle.
  const powerCellPart = COMM_PARTS.find((p) => p.id === 'comm_battery');
  if (powerCellPart) {
    powerCellEntry = spawnPickup(scene, planet, {
      ...powerCellPart, beacon: true, locked: true,
      dir: POND_DIRECTION.clone(),
    });
    const raftWorld = _raftWorldPos();
    powerCellEntry.group.position.copy(raftWorld);
    powerCellEntry.group.quaternion.setFromUnitVectors(UP_Y, POND_DIRECTION);
    powerCellEntry.worldPos.copy(raftWorld);
  }

  // ---- Lure Bloom pickup on the grass ----
  // Set back from the shore so its link to the pond isn't immediately
  // obvious — the player finds a strange glowing flower out in the
  // grass, then works out it's what the fish respond to.
  const lureAngleLocal = Math.PI * 0.15;
  const lureDistLocal  = SHORE_R + 10;
  const lureWorldOffset = new THREE.Vector3(
    Math.cos(lureAngleLocal) * lureDistLocal, 0,
    Math.sin(lureAngleLocal) * lureDistLocal,
  ).applyQuaternion(group.quaternion);
  const lureWorld = group.position.clone().add(lureWorldOffset);
  spawnPickup(scene, planet, {
    id:    'lure_bloom',
    name:  'Lure Bloom',
    color: 0x5ce3ff,
    shape: 'bloom',
    icon:  '✺',
    dir:   lureWorld.clone().normalize(),
  });

  return { group, worldPos: pondWorldPos };
}

// World-space position of the raft (used to place/track the Power Cell).
function _raftWorldPos() {
  const v = raftLocal.clone().applyQuaternion(pondGroup.quaternion);
  return v.add(pondGroup.position);
}

function makeFishMesh(material) {
  const group = new THREE.Group();

  // Elongated, flattened body — reads as a fish silhouette from above.
  const body = new THREE.Mesh(
    new THREE.SphereGeometry(0.11, 10, 8), material,
  );
  body.scale.set(0.55, 0.35, 1.6);
  group.add(body);

  // Tail — flat triangular fin at the back.
  const tail = new THREE.Mesh(
    new THREE.ConeGeometry(0.08, 0.14, 3), material,
  );
  tail.rotation.x = -Math.PI / 2;
  tail.rotation.z = Math.PI;                // point tail back along -Z
  tail.scale.set(0.65, 1, 0.35);
  tail.position.set(0, 0, -0.18);
  group.add(tail);

  return group;
}

// Smallest signed angle from `from` to `to` (−π..π).
function angleDelta(from, to) {
  let d = to - from;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

export function updatePond(character) {
  if (!discovered && pondWorldPos && character) {
    const dist = character.position.distanceTo(pondWorldPos);
    if (dist < DISCOVERY_RANGE) {
      discovered = true;
      events.emit('pond:discovered', { position: pondWorldPos.clone() });
    }
  }

  if (!waterGeo) return;

  const t = performance.now() / 1000;
  const dt = 1 / 60;      // the pond loop isn't dt-bound; fixed tick is fine

  // ---- Ripple the water surface ----
  const pos = waterGeo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const ripple =
        Math.sin(x * 2.0 + t * 1.4) * 0.018
      + Math.sin(z * 2.5 - t * 1.1) * 0.013
      + Math.sin((x + z) * 3.1 + t * 0.7) * 0.008;
    pos.setY(i, waterBaseY[i] + ripple);
  }
  pos.needsUpdate = true;
  waterGeo.computeVertexNormals();

  // ---- Lily pads ----
  for (const p of lilyPads) {
    const b = Math.sin(t * 1.3 + p.phase) * 0.018
            + Math.sin(t * 0.7 + p.phase * 1.7) * 0.008;
    p.group.position.y = p.baseY + b;
    p.group.rotation.y += Math.sin(t * 0.5 + p.phase) * 0.0015;
  }

  // ---- Raft drift (lure-cast puzzle) ----
  if (raftGroup) {
    if (lureState === 'cast') {
      const elapsed = t - castStartTime;
      const p = Math.min(1, elapsed / CAST_DURATION);
      const eased = 1 - Math.pow(1 - p, 2);         // easeOutQuad
      raftLocal.lerpVectors(raftStartLocal, raftTargetLocal, eased);
      if (p >= 1) lureState = 'arrived';
    }
    // Always apply gentle bob + position (works at rest too).
    const bob = Math.sin(t * 1.2) * 0.012;
    raftGroup.position.set(raftLocal.x, raftLocal.y + bob, raftLocal.z);

    // Keep the Power Cell pickup riding on the raft.
    if (powerCellEntry) {
      const raftWorld = _raftWorldPos();
      raftWorld.y += 0; // raft local y already baked into raftLocal
      powerCellEntry.group.position.copy(raftWorld);
      powerCellEntry.worldPos.copy(raftWorld);
    }
  }

  // ---- Fish — wander + boundary correction ----
  for (const f of fishList) {
    // Heading drift. Every few seconds pick a new turn rate so paths are
    // curvy rather than straight.
    f.turnTimer -= dt;
    if (f.turnTimer <= 0) {
      f.turnRate = (Math.random() - 0.5) * 0.9;
      f.turnTimer = 1 + Math.random() * 2.5;
    }

    // Boundary correction — if we're close to the irregular rim, steer
    // back toward the pond centre.
    const distFromCenter = Math.sqrt(f.x * f.x + f.z * f.z);
    const angleFromCenter = Math.atan2(f.z, f.x);
    const rimR = waterRadiusAt(angleFromCenter);
    const safeR = rimR * 0.78;              // start turning before the edge

    let turn = f.turnRate;
    if (distFromCenter > safeR) {
      const inward = Math.atan2(-f.z, -f.x);
      const delta = angleDelta(f.heading, inward);
      // Scale turn strength with how deep into the margin we are.
      const push = Math.min(1, (distFromCenter - safeR) / (rimR - safeR));
      turn = delta * (1.5 + 3.5 * push);
    }
    f.heading += turn * dt;

    // Advance.
    f.x += Math.cos(f.heading) * f.speed * dt;
    f.z += Math.sin(f.heading) * f.speed * dt;

    // Write transform. Fish mesh default forward is +Z, so rotation.y is
    // π/2 − heading (see sphere→Y rotation math in footprints/creatures).
    f.mesh.position.x = f.x;
    f.mesh.position.z = f.z;

    // Gentle tail wiggle — oscillate the yaw a little around the heading.
    f.wigglePhase += dt * 10;
    const wiggle = Math.sin(f.wigglePhase) * 0.25;
    f.mesh.rotation.y = (Math.PI / 2 - f.heading) + wiggle;

    // Slight vertical bob + subtle roll so it feels alive.
    f.mesh.position.y = 0.006 + Math.sin(f.wigglePhase * 0.5) * 0.003;
    f.mesh.rotation.z = Math.sin(f.wigglePhase * 0.7) * 0.08;
  }
}
